// =============================================================================
// app/src/lib/placa.ts — normalização e validação de placa brasileira.
//
// Dois formatos convivem na rua:
//   antiga    AAA-9999  (até 2018)
//   Mercosul  AAA9A99   (a 5ª posição virou letra)
// O CHECK do banco aceita os dois: '^[A-Z]{3}[0-9][0-9A-Z][0-9]{2}$'.
// Normalizada = maiúscula, só [A-Z0-9] — 'abc-1234' e 'ABC1234' são a mesma.
// =============================================================================
import type { DadosDaConsultaDePlaca, VersaoFipeDaConsulta } from '@core/types';

/** Remove tudo que não é letra/número e sobe para maiúscula. */
export const normalizarPlaca = (texto: string): string =>
  texto.toUpperCase().replace(/[^A-Z0-9]/g, '');

const RE_PLACA = /^[A-Z]{3}[0-9][0-9A-Z][0-9]{2}$/;

/** true se, depois de normalizar, é uma placa válida (antiga ou Mercosul). */
export const placaValida = (texto: string): boolean =>
  RE_PLACA.test(normalizarPlaca(texto));

/** 'ABC1D23' → 'ABC-1D23' (como se lê no balcão). Inválida volta como veio. */
export const formatarPlaca = (texto: string): string => {
  const n = normalizarPlaca(texto);
  return RE_PLACA.test(n) ? `${n.slice(0, 3)}-${n.slice(3)}` : texto;
};

/** Mercosul tem letra na 5ª posição. */
export const ehMercosul = (texto: string): boolean => {
  const n = normalizarPlaca(texto);
  return RE_PLACA.test(n) && /[A-Z]/.test(n[4] ?? '');
};

// --- O que a consulta externa disse, para o balcão ler (18/09/2026) -----------
// vehicle_plates.consulta_api (migração 076) guarda o recorte da resposta com os
// nomes de campo do fornecedor. Aqui ele vira rótulo e valor — nunca estrutura
// de código na tela. A LEITURA do sistema (quais versões o documento não
// descarta) não é refeita aqui: vem pronta do agente, que é quem decidiu.

/** A leitura do sistema sobre a consulta guardada, como o agente a devolve
 *  (infra/agente/placa.mjs, tratamentoDaConsulta). */
export interface TratamentoDaPlaca {
  /** Cilindrada que o documento afirma, em litros: "2.0". */
  litros: string | null;
  /** Câmbio que o documento afirma; null quando não diz. */
  cambio: 'AUT' | 'MEC' | null;
  /** Códigos FIPE que o documento NÃO descarta. */
  compativeis: string[];
  /** Códigos FIPE que a loja tem no catálogo; null quando não deu para ler. */
  no_catalogo: string[] | null;
}

export interface LinhaDaFicha { rotulo: string; valor: string }
export interface SecaoDaFicha { titulo: string; linhas: LinhaDaFicha[] }

/** O Denatran preenche o que não se aplica com texto, e isso no balcão é ruído. */
const RUIDO = /^(N[AÃ]O APLIC[AÁ]VEL|N[AÃ]O INFORMADO|-+|0)$/i;

/**
 * Valor que vale mostrar, ou null. Aceita `unknown` DE PROPÓSITO: o agente grava
 * texto, mas consulta_api é gravável por qualquer perfil da equipe (a mesma RLS
 * da placa), e um número ou objeto ali não pode derrubar a tela de venda.
 */
const util = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
  return s && !RUIDO.test(s) ? s : null;
};

const objeto = <T extends object>(v: unknown): Partial<T> =>
  (v && typeof v === 'object' && !Array.isArray(v) ? v : {}) as Partial<T>;

/** "1984" → "1.984 cm³". Número estranho volta como veio: não se inventa unidade. */
export function cilindradaLegivel(cm3: string | null | undefined): string | null {
  const s = util(cm3);
  if (!s) return null;
  const n = Number(s.replace(/\D/g, ''));
  if (!/^\d+$/.test(s) || !Number.isFinite(n) || n < 500 || n > 9000) return s;
  return `${n.toLocaleString('pt-BR')} cm³`;
}

/**
 * A consulta em seções de rótulo e valor, na ordem em que o balcão pergunta:
 * que carro é, que motor tem, que corpo tem. Vazio, "NAO APLICAVEL" e o
 * submodelo ou a versão que só repetem o modelo (o Denatran escreve "JETTA"
 * nos três) não aparecem; seção sem linha nenhuma também não.
 */
export function fichaDaConsulta(dadosGuardados: DadosDaConsultaDePlaca): SecaoDaFicha[] {
  const d = objeto<DadosDaConsultaDePlaca>(dadosGuardados);
  const e = objeto<NonNullable<DadosDaConsultaDePlaca['extra']>>(d.extra);
  const fabricacao = util(e.ano_fabricacao) ?? util(d.ano);
  const anoModelo = util(d.anoModelo) ?? util(e.ano_modelo);
  const ano = fabricacao && anoModelo && fabricacao !== anoModelo
    ? `${fabricacao}/${anoModelo}`
    : anoModelo ?? fabricacao;

  // Só modelo, submodelo e versão se repetem por costume do Denatran. Nenhum
  // outro valor igual é descartado: 2 passageiros e 2 eixos são duas linhas.
  const modelo = util(d.MODELO) ?? util(d.modelo);
  const submodelo = util(d.SUBMODELO);
  const versao = util(d.VERSAO);
  const repete = (a: string | null, ...outros: Array<string | null>) =>
    a !== null && outros.some((b) => b !== null && b.toUpperCase() === a.toUpperCase());

  const secoes: Array<{ titulo: string; linhas: Array<[string, string | null]> }> = [
    {
      titulo: 'Identificação',
      linhas: [
        ['Marca', util(d.MARCA) ?? util(d.marca)],
        ['Modelo', modelo],
        ['Submodelo', repete(submodelo, modelo) ? null : submodelo],
        ['Versão', repete(versao, modelo, submodelo) ? null : versao],
        ['Ano', ano],
        ['Cor', util(d.cor)],
        ['Procedência', util(d.origem) ?? util(e.nacionalidade)],
      ],
    },
    {
      titulo: 'Motor',
      linhas: [
        ['Cilindrada', cilindradaLegivel(e.cilindradas)],
        ['Combustível', util(e.combustivel)],
      ],
    },
    {
      titulo: 'Carroceria',
      linhas: [
        ['Tipo', util(e.tipo_veiculo)],
        ['Espécie', util(e.especie)],
        ['Segmento', util(e.sub_segmento) ?? util(e.segmento)],
        ['Carroceria', util(e.tipo_carroceria)],
        ['Passageiros', util(e.quantidade_passageiro)],
        ['Eixos', util(e.eixos)],
      ],
    },
  ];

  return secoes
    .map(({ titulo, linhas }) => ({
      titulo,
      linhas: linhas.flatMap(([rotulo, valor]) => (valor ? [{ rotulo, valor }] : [])),
    }))
    .filter((s) => s.linhas.length > 0);
}

export interface VersaoDaConsulta {
  /** `${codigo_marca}-${codigo_modelo}` — a chave de vehicle_catalog. */
  codigo: string;
  /** Como a FIPE escreve: "JETTA Comfortline 2.0 T.Flex 8V 4p Tipt." */
  nome: string;
  /** "R$ 40.000,00 (setembro de 2026)", quando veio. */
  valor: string | null;
  /** O documento não a descarta. null: sem a leitura do sistema. */
  compativel: boolean | null;
  /** A loja a tem no catálogo (a busca guiada oferece). null: não se sabe. */
  noCatalogo: boolean | null;
}

/**
 * As versões FIPE que a consulta sugeriu, cada uma com a leitura do sistema:
 * as compatíveis com o documento primeiro, e dentro de cada grupo a ordem em
 * que vieram. Sem `tratamento` (agente fora do ar, leitura direta da tabela),
 * a lista sai inteira e sem marca nenhuma — melhor que marcar errado.
 */
export function versoesDaConsulta(
  d: DadosDaConsultaDePlaca,
  tratamento: TratamentoDaPlaca | null | undefined,
): VersaoDaConsulta[] {
  const vistas = new Set<string>();
  const lista: VersaoDaConsulta[] = [];
  const fipe = objeto<NonNullable<DadosDaConsultaDePlaca['fipe']>>(objeto<DadosDaConsultaDePlaca>(d).fipe);
  for (const item of Array.isArray(fipe.dados) ? fipe.dados : []) {
    const v = objeto<VersaoFipeDaConsulta>(item);
    const nome = util(v.texto_modelo);
    const codigo = `${v.codigo_marca}-${v.codigo_modelo}`;
    if (!nome || vistas.has(codigo)) continue;
    vistas.add(codigo);
    const preco = util(v.texto_valor);
    const mes = util(v.mes_referencia);
    lista.push({
      codigo,
      nome,
      valor: preco ? (mes ? `${preco} (${mes})` : preco) : null,
      compativel: tratamento ? tratamento.compativeis.includes(codigo) : null,
      noCatalogo: tratamento?.no_catalogo ? tratamento.no_catalogo.includes(codigo) : null,
    });
  }
  return [...lista.filter((v) => v.compativel), ...lista.filter((v) => !v.compativel)];
}
